import React from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from '@emotion/styled';

import Image from './Image';
import { rem } from '../utilities/style';
import TextLink from './TextLink';

// There won't be a circular reference because imageCaption does not allow embedded entries
// eslint-disable-next-line import/no-cycle
import TextRenderer from './TextRenderer';

const ImageWrapper = styled.div`
  margin: ${rem(20)} 0;

  ${({ width }) => width && `
    max-width: ${width}px;
  `}

  ${({ center }) => center && `
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    clear: both;
  `}

  ${({ float }) => float && `
    float: ${float};
    margin-${float}: 0;
    margin-${float === 'left' ? 'right' : 'left'}: ${rem(20)};
  `}
`;

const CaptionWrapper = styled.div`
  padding-top: 0.75rem;
  font-size: ${rem(13)};
`;

const RichTextImage = ({
  image,
  imageLink,
  alignment,
  width,
  imageCaption
}) => {
  let float = null;
  // set the default width of the image if a fixed width is null
  const adjustedWidth = width || image?.file?.details.image.width;

  if (alignment === 'Left') {
    float = 'left';
  } else if (alignment === 'Right') {
    float = 'right';
  }
  if (!image) {
    return null;
  }

  const imageElement = image.gatsbyImageData ? (
    <GatsbyImage
      image={image.gatsbyImageData}
      alt={image.description}
    />
  ) : (
    <img
      src={image.file?.url}
      alt={image.description}
      width={image.file?.details.image.width}
      height={image.file?.details.image.height}
    />
  );

  return (
    <ImageWrapper float={float} center={alignment === 'Center'} width={adjustedWidth} imageCaption={imageCaption} imageLink={imageLink}>
      {imageLink ? (
        <TextLink to={imageLink} target="_blank" rel="noopener noreferrer">
          {imageElement}
        </TextLink>
      ) : (
        imageElement
      )}
      {imageCaption && (
        <CaptionWrapper>
          <TextRenderer text={imageCaption} richText />
        </CaptionWrapper>
      )}
    </ImageWrapper>
  );
};

RichTextImage.propTypes = {
  image: Image.propTypes.image,
  imageLink: PropTypes.string,
  alignment: PropTypes.oneOf(['Default', 'Left', 'Center', 'Right']),
  width: PropTypes.number,
  imageCaption: PropTypes.shape({
    raw: PropTypes.string
  })
};

export default RichTextImage;
